import {
  type GetWorkspaceAssetsArgs,
  type GetWorkspaceFoldersArgs,
  type SearchResult,
  useInfiniteScrollFromCache,
  useLazyGetRecordingQuery,
  useLazyGetWorkspaceAssetsQuery,
  useLazyGetWorkspaceFoldersQuery,
  workspaceApi,
} from '@carboncopyco/data';
import {
  type Entity,
  type IRecording,
  RecordingPostprocessStateTypes,
} from '@carboncopyco/types';
import { SkeletonImageLoading } from '@carboncopyco/ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { InfiniteList } from '../../InfiniteList';
import { useUrlState } from '../../utils';
import { AssetGridSection } from './AssetGridSection';
import { useWorkspacePageProps } from './WorkspacePageContext';
import { WorkspacePageControls } from './WorkspacePageControls';
import { WorkspacePageEmptySearch } from './WorkspacePageEmptySearch';
import { WorkspacePageEmptyState } from './WorkspacePageEmptyState';
import { WorkspacePageFolderGrid } from './WorkspacePageFolderGrid';
import { WorkspacePageTableView } from './WorkspacePageTableView';
import { ALL_ENTITIES, INITIAL_URL_STATE } from './consts';
import type { UrlState } from './types';

type DirtyRef = {
  isDirty: boolean;
  resetControls: () => void;
};

const PENDING_CLONES_FETCHING_INTERVAL_MS = 3000;

export const WorkspacePageBody = () => {
  const dispatch = useAppDispatch();
  const { workspaceId, organizationId, setFolderId } = useWorkspacePageProps();

  const [urlState] = useUrlState<UrlState>(INITIAL_URL_STATE);
  const { viewMode, entities, creators, query, sort } = urlState;
  const dirtyRef = useRef<DirtyRef>({
    isDirty: false,
    resetControls: () => undefined,
  });
  const isDirty = dirtyRef?.current?.isDirty;

  const fetchArgs: GetWorkspaceAssetsArgs = useMemo(
    () => ({
      organizationId,
      workspaceId,
      creators: creators ? creators?.split(',') : undefined,
      sort,
      deletedEntities: false,
      entities: entities
        ? (entities.split(',') as Entity[])
        : (ALL_ENTITIES as Entity[]),
      query: query && query,
    }),
    [workspaceId, organizationId, entities, creators, query, sort]
  );

  const fetchFolderArgs: GetWorkspaceFoldersArgs = useMemo(
    () => ({
      organizationId,
      workspaceId,
      creators: creators ? creators?.split(',') : undefined,
      sort,
      query: query && query,
      limit: 50,
      entities: entities ? (entities.split(',') as Entity[]) : [],
    }),
    [workspaceId, organizationId, entities, creators, query, sort]
  );

  const [
    getActiveFolders,
    {
      data: activeFolders,
      isLoading: isLoadingFolders,
      isFetching: isFetchingFolders,
      isSuccess: isSuccessFolders,
    },
  ] = useLazyGetWorkspaceFoldersQuery();

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    isEmpty: noAssets,
  } = useInfiniteScrollFromCache<SearchResult, GetWorkspaceAssetsArgs>(
    useLazyGetWorkspaceAssetsQuery,
    fetchArgs
  );

  const [fetchClone] = useLazyGetRecordingQuery();

  const pendingClones = useMemo(
    () =>
      (items || []).filter(
        (item) =>
          item.entityType === 'record' &&
          (item as IRecording).postprocessState ===
            RecordingPostprocessStateTypes.pending
      ) as IRecording[],
    [items]
  );

  useEffect(() => {
    setFolderId('0');
  }, [setFolderId]);

  useEffect(() => {
    fetchFirstBatch();
    getActiveFolders(fetchFolderArgs);
  }, [fetchArgs, fetchFirstBatch, fetchFolderArgs, getActiveFolders]);

  const markClonePostprocessingAsDone = useCallback(
    (cloneId: number | string) => {
      dispatch(
        workspaceApi.util.updateQueryData(
          'getWorkspaceAssets',
          fetchArgs,
          (draft) => {
            const item = draft.items.find((element) => element.id === cloneId);
            if (!item || item?.entityType !== 'record') {
              return;
            }
            const clone = item as IRecording;
            clone.postprocessState = RecordingPostprocessStateTypes.done;
          }
        )
      );
    },
    [dispatch, fetchArgs]
  );

  useEffect(() => {
    if (pendingClones?.length < 1) {
      return;
    }
    const interval = setInterval(async () => {
      for (const pendingClone of pendingClones) {
        const { data: clone } = await fetchClone({
          organizationId,
          id: pendingClone.id,
        });
        if (clone.postprocessState === RecordingPostprocessStateTypes.done) {
          markClonePostprocessingAsDone(pendingClone.id);
        }
      }
    }, PENDING_CLONES_FETCHING_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [
    fetchClone,
    markClonePostprocessingAsDone,
    organizationId,
    pendingClones,
  ]);

  const hasFolders = activeFolders?.length > 0;
  const noAssetNoFolder = noAssets && !hasFolders;
  const wsIsEmpty = noAssetNoFolder && !isDirty;
  const searchIsEmpty = isDirty && noAssetNoFolder;

  if (wsIsEmpty && !isLoadingFolders && !isLoading) {
    return <WorkspacePageEmptyState />;
  }

  return (
    <>
      <WorkspacePageControls ref={dirtyRef} />
      {searchIsEmpty && isSuccessFolders ? (
        <WorkspacePageEmptySearch
          resetControls={dirtyRef.current.resetControls}
        />
      ) : (
        <div className="w-full flex justify-center overflow-y-hidden flex-auto py-5">
          <div className="w-ds-page h-full flex overflow-y-hidden relative">
            {viewMode === 'tiles' ? (
              <InfiniteList
                error={
                  error && {
                    message: 'Error fetching assets',
                  }
                }
                isLoading={isLoading || isLoadingFolders || isFetchingFolders}
                isLoadingBatch={isLoadingBatch}
                loader={
                  <SkeletonImageLoading
                    cols={4}
                    rows={4}
                  />
                }
                onReachBottom={fetchNextBatch}
              >
                {hasFolders && <WorkspacePageFolderGrid data={activeFolders} />}
                <AssetGridSection items={items} />
              </InfiniteList>
            ) : (
              <WorkspacePageTableView
                items={(items || []) as SearchResult[]}
                folders={activeFolders}
                isLoading={isLoading}
                isLoadingBatch={isLoadingBatch}
                error={error}
                fetchNextBatch={fetchNextBatch}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
